import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  artifactCards,
  mockPatient,
  globalComplianceText,
} from "@/mocks/demo-flow";
import ClinicHeaderUser from "@/components/feature/ClinicHeaderUser";

export default function AIArtifactsPage() {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [proceeding, setProceeding] = useState(false);

  const toggleCard = (id: string) => {
    setExpandedCard((prev) => (prev === id ? null : id));
  };

  const handleProceed = () => {
    setProceeding(true);
    setTimeout(() => {
      navigate("/clinic-roi");
    }, 800);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <ClinicHeaderUser />
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8 relative">
        {/* Subtle warm background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/15 blur-3xl"></div>
          <div className="absolute bottom-20 -left-40 w-[400px] h-[400px] rounded-full bg-primary-100/10 blur-3xl"></div>
        </div>

        {/* Page Header */}
        <div className="relative z-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
            <span className="w-7 h-7 flex items-center justify-center text-accent-600">
              <i className="ri-file-copy-line text-xl"></i>
            </span>
          </div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-2">
            AI-Generated Administrative Artifacts
          </h1>
          <p className="text-xs md:text-sm text-foreground-500 max-w-xl mx-auto">
            (Pending Clinical Review) — Three interactive document previews
            generated seamlessly from the patient&apos;s home intake data and
            telehealth summary.
          </p>
        </div>

        {/* Patient Context Badge */}
        <div className="relative z-10 flex items-center justify-center">
          <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white border border-background-200">
            <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-primary-700">
                {mockPatient.fullName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </span>
            </span>
            <div className="text-left">
              <p className="text-xs font-semibold text-foreground-800">
                {mockPatient.fullName}
              </p>
              <p className="text-[10px] text-foreground-400">
                {mockPatient.amputationLevel} · K{mockPatient.kLevel} ·{" "}
                {mockPatient.insuranceProvider}
              </p>
            </div>
            <span className="w-px h-6 bg-background-200" />
            <span className="text-[10px] text-foreground-500">
              Artifacts generated from intake pipeline
            </span>
          </div>
        </div>

        {/* Three Document Cards — Side by Side */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {artifactCards.map((card) => {
            const isExpanded = expandedCard === card.id;

            return (
              <div
                key={card.id}
                className={`rounded-2xl border-2 transition-all duration-300 flex flex-col ${
                  isExpanded
                    ? "border-primary-300 bg-primary-50/20 shadow-[0_0_0_1px_rgba(0,0,0,0.02)]"
                    : "border-background-200 bg-white hover:border-background-300"
                }`}
              >
                {/* Card Top Accent Bar */}
                <div
                  className={`h-1 w-full rounded-t-2xl ${card.accentClass.split(" ")[0]}`}
                />

                <button
                  type="button"
                  onClick={() => toggleCard(card.id)}
                  className="w-full text-left p-5 md:p-6 cursor-pointer flex-1 flex flex-col"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span
                        className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${card.accentClass.split(" ")[0]} ${card.accentClass.split(" ")[1]}`}
                      >
                        <i className={`${card.icon} text-base`}></i>
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 leading-snug">
                          {card.title}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold mt-1.5 ${card.accentClass}`}
                        >
                          {card.badgeText}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-300 transition-transform duration-200 ml-2 ${isExpanded ? "rotate-180" : ""}`}
                    >
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </span>
                  </div>

                  {/* Collapsed Preview — Show first 3 fields */}
                  {!isExpanded && (
                    <div className="space-y-1.5 flex-1">
                      {card.sections.slice(0, 3).map((s, i) => (
                        <p
                          key={i}
                          className="text-[11px] text-foreground-500 truncate"
                        >
                          <span className="font-semibold text-foreground-700">
                            {s.label}:
                          </span>{" "}
                          {s.value}
                        </p>
                      ))}
                      <p className="text-[10px] text-foreground-300 mt-2 pt-2 border-t border-background-100">
                        + {card.sections.length - 3} more fields — click to
                        expand
                      </p>
                    </div>
                  )}

                  {/* Expanded Content — Full document view */}
                  {isExpanded && (
                    <div className="space-y-3 pt-1 animate-fadeSlideIn flex-1">
                      <div className="rounded-xl border border-background-200 bg-background-50 p-4">
                        {card.sections.map((s, i) => (
                          <div
                            key={i}
                            className={`flex items-start gap-3 py-2 ${i !== card.sections.length - 1 ? "border-b border-background-100" : ""}`}
                          >
                            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-foreground-300" />
                            </span>
                            <div className="min-w-0">
                              <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold block mb-0.5">
                                {s.label}
                              </span>
                              <span className="text-[11px] md:text-xs text-foreground-600 leading-relaxed">
                                {s.value}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Mini action — review stamp */}
                      <div className="flex items-center justify-end gap-2 text-[10px] text-foreground-400">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-information-line text-[10px]"></i>
                        </span>
                        AI-generated draft — physician review required
                      </div>
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Physician Review Callout */}
        <div className="relative z-10 rounded-2xl border-2 border-dashed border-accent-300 bg-accent-50/50 p-5 md:p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1 flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-edit-line text-base"></i>
              </span>
              <div>
                <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 mb-1">
                  Physician Review Required
                </h3>
                <p className="text-[11px] md:text-xs text-foreground-500 leading-relaxed">
                  Dr. A. Paliga, MD must independently review and sign off on
                  all three artifacts above. These AI-generated documents are
                  strictly administrative drafts — no prosthetic device may be
                  fabricated without explicit physician approval.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap flex-shrink-0">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-check-line text-[10px]"></i>
              </span>
              Edit &amp; Sign-Off
            </span>
          </div>
        </div>

        {/* Proceed CTA */}
        <div className="relative z-10 text-center space-y-3">
          {!proceeding ? (
            <button
              type="button"
              onClick={handleProceed}
              className="inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Click Here to Proceed to Clinic ROI Dashboard
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </span>
            </button>
          ) : (
            <div className="inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-accent-50 border border-accent-200 animate-fadeSlideIn">
              <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                <i className="ri-loader-4-line text-base animate-spin"></i>
              </span>
              <span className="text-sm font-semibold text-accent-700">
                Loading Clinic ROI Dashboard...
              </span>
            </div>
          )}
          <p className="text-[10px] md:text-[11px] text-foreground-400">
            Artifacts are committed to the patient record. The clinic will
            access them upon authorization.
          </p>
        </div>
      </main>

      {/* Compliance Footer */}
      <footer className="w-full bg-background-100 border-t border-background-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
              <i className="ri-information-line text-[11px]"></i>
            </span>
            <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
              {globalComplianceText}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
